import React, { useEffect, useMemo, useState } from 'react'
import { Box, TextField, Typography, ToggleButton, ToggleButtonGroup, MenuItem, Select } from '@mui/material'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import { intelApi } from './api'
import { intelColors, formatNumber } from './components/intelTheme'

const DIRS = [
  { val: 'ALL', label: 'All' },
  { val: 'FORWARD', label: 'Forward' },
  { val: 'REVERSE', label: 'Reverse' },
]
const PAYMENT_MODES = [
  { val: 'ALL', label: 'All' },
  { val: 'COD', label: 'COD' },
  { val: 'PREPAID', label: 'Prepaid' },
  { val: 'BOTH', label: 'Both' },
]

export default function WeightSlabs() {
  const [parentCp, setParentCp] = useState('')
  const [accountCode, setAccountCode] = useState('')
  const [serviceType, setServiceType] = useState('')
  const [direction, setDirection] = useState('ALL')
  const [paymentMode, setPaymentMode] = useState('ALL')
  // Quick Check is a client-side highlight (not a server filter): green-flag rows whose
  // max dead weight can carry the entered package weight.
  const [quickCheckKg, setQuickCheckKg] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  // Dropdown option lists — parent + service type load once; courier codes cascade off parent.
  const [parentOptions, setParentOptions] = useState([])
  const [accountCodeOptions, setAccountCodeOptions] = useState([])
  const [serviceTypeOptions, setServiceTypeOptions] = useState([])

  // Form options (parent list + service types) — single round-trip on mount.
  useEffect(() => {
    let cancel = false
    intelApi
      .formOptions()
      .then((res) => {
        if (cancel) return
        const o = res?.data?.responseObj || res?.data?.data || res?.data || {}
        setParentOptions(o.parentCps || [])
        setServiceTypeOptions(o.serviceTypes || [])
      })
      .catch(() => {})
    return () => {
      cancel = true
    }
  }, [])

  // Courier codes cascade off the chosen parent.
  useEffect(() => {
    if (!parentCp) {
      setAccountCodeOptions([])
      return
    }
    let cancel = false
    intelApi
      .accountCodes({ parent_cp: parentCp })
      .then((res) => {
        if (cancel) return
        const b = res?.data?.responseObj || res?.data?.data || res?.data || {}
        setAccountCodeOptions((b.accountCodes || []).map((c) => c.accountCode || c).filter(Boolean))
      })
      .catch(() => !cancel && setAccountCodeOptions([]))
    return () => {
      cancel = true
    }
  }, [parentCp])

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .weights({
        parentCp,
        accountCode,
        serviceType,
        direction,
        paymentMode,
      })
      .then((res) => !cancel && setData(res?.data?.responseObj || res?.data?.data || res?.data))
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [parentCp, accountCode, serviceType, direction, paymentMode])

  const kpis = data?.kpis || {}
  const rows = data?.rows || []
  // Quick Check — package dead weight: highlight + count couriers that can carry it.
  const qcKg = parseFloat(quickCheckKg)
  const qcActive = !isNaN(qcKg) && qcKg > 0
  const qcMatched = qcActive ? rows.filter((r) => (r.maxDeadWeightKg || 0) >= qcKg).length : 0

  const columns = useMemo(
    () => [
      { key: 'code', header: 'Courier Code', render: (r) => <code style={{ background: '#f1f3f5', padding: '1px 5px', borderRadius: 3 }}>{r.cpAccountCode}</code> },
      { key: 'parent', header: 'Parent Courier', render: (r) => r.parent },
      { key: 'st', header: 'Service Type', render: (r) => r.serviceTypePrimary || '—' },
      { key: 'dir', header: 'Direction', render: (r) => <Badge color={r.direction === 'RVP' ? 'orange' : 'blue'}>{r.direction === 'RVP' ? 'Reverse' : r.direction === 'FORWARD' ? 'Forward' : r.direction || '—'}</Badge> },
      { key: 'dw', header: 'Max Dead Wt (kg)', render: (r) => <span style={{ fontWeight: 600 }}>{r.maxDeadWeightKg ?? '—'}</span> },
      { key: 'dim', header: 'Max Dims', render: (r) => <span style={{ fontSize: 11 }}>{r.maxDimsLabel || '—'}</span> },
      { key: 'vol', header: 'Vol Divisor', render: (r) => r.volumetricDivisor ?? '—' },
      { key: 'volwt', header: 'Vol Wt Equiv', render: (r) => r.volumetricWeightEquiv ?? '—' },
      { key: 'bill', header: 'Max Billable (kg)', render: (r) => <span style={{ fontWeight: 600 }}>{r.maxBillableWeightKg ?? '—'}</span> },
      { key: 'rule', header: 'Chargeable Rule', render: (r) => (r.chargeableWeightRule ? <Badge color={r.chargeableWeightRule === 'HIGHER' ? 'orange' : 'blue'}>{r.chargeableWeightRule}</Badge> : '—') },
      { key: 'pay', header: 'Payment Mode', render: (r) => { const m = r.paymentMode; const c = m === 'BOTH' ? 'green' : m === 'COD' ? 'orange' : m === 'PREPAID' ? 'blue' : 'grey'; return <Badge color={c}>{m && m !== 'NA' ? m : '—'}</Badge> } },
    ],
    []
  )

  return (
    <IntelLayout title='Weight & Dimension Limits'>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', mb: 2 }}>
        <KpiCard loading={loading} color='blue' label='Active Courier Codes' value={formatNumber(kpis.activeCourierCodes?.value)} />
        <KpiCard loading={loading} color='orange' label='Max Dead Weight (per piece)' value={`${kpis.maxDeadWeight?.value || '—'} kg`} sub='Highest single-piece limit across active codes' />
        <KpiCard loading={loading} color='green' label='COD-Enabled Codes' value={formatNumber(kpis.codEnabledCodes?.value)} />
      </Box>

      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${intelColors.border}`,
          borderRadius: '8px',
          padding: '14px 18px',
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Parent Courier</Typography>
          <Select size='small' displayEmpty value={parentCp} onChange={(e) => { setParentCp(e.target.value); setAccountCode('') }} sx={{ width: 180, fontSize: 12, '& .MuiSelect-select': { padding: '7px 10px' } }}>
            <MenuItem value=''>All Parents</MenuItem>
            {parentOptions.map((p) => <MenuItem key={p} value={p} sx={{ fontSize: 12 }}>{p}</MenuItem>)}
          </Select>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Courier Code</Typography>
          <Select size='small' displayEmpty disabled={!parentCp} value={accountCode} onChange={(e) => setAccountCode(e.target.value)} sx={{ width: 200, fontSize: 12, '& .MuiSelect-select': { padding: '7px 10px' } }}>
            <MenuItem value=''>{parentCp ? 'All Codes' : 'Select a parent first'}</MenuItem>
            {accountCodeOptions.map((c) => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
          </Select>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Service Type</Typography>
          <Select size='small' value={serviceType} onChange={(e) => setServiceType(e.target.value)} displayEmpty sx={{ width: 220, fontSize: 12, '& .MuiSelect-select': { padding: '7px 10px' } }}>
            <MenuItem value=''>All</MenuItem>
            {serviceTypeOptions.map((s) => <MenuItem key={s.value} value={s.value} sx={{ fontSize: 12 }}>{s.label || s.value}</MenuItem>)}
          </Select>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Direction</Typography>
          <ToggleButtonGroup exclusive size='small' value={direction} onChange={(_, v) => v && setDirection(v)} sx={{ '& .MuiToggleButton-root': { padding: '4px 12px', fontSize: 11, fontWeight: 600, textTransform: 'none', borderRadius: '12px !important', mr: '4px', ml: '0 !important', border: '1px solid #d0d7de' }, '& .Mui-selected': { background: '#e3f2fd !important', color: `${intelColors.primary} !important`, borderColor: `${intelColors.primary} !important` } }}>
            {DIRS.map((d) => <ToggleButton key={d.val} value={d.val}>{d.label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Payment Mode</Typography>
          <ToggleButtonGroup exclusive size='small' value={paymentMode} onChange={(_, v) => v && setPaymentMode(v)} sx={{ '& .MuiToggleButton-root': { padding: '4px 12px', fontSize: 11, fontWeight: 600, textTransform: 'none', borderRadius: '12px !important', mr: '4px', ml: '0 !important', border: '1px solid #d0d7de' }, '& .Mui-selected': { background: '#e3f2fd !important', color: `${intelColors.primary} !important`, borderColor: `${intelColors.primary} !important` } }}>
            {PAYMENT_MODES.map((d) => <ToggleButton key={d.val} value={d.val}>{d.label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Quick Check — Package Dead Wt (kg)</Typography>
          <TextField size='small' type='number' value={quickCheckKg} onChange={(e) => setQuickCheckKg(e.target.value)} placeholder='e.g. 5.0' inputProps={{ min: 0, step: 0.5 }} sx={{ width: 200, '& input': { fontSize: 12, padding: '7px 10px' } }} />
        </Box>
        <Box
          onClick={() => { setParentCp(''); setAccountCode(''); setServiceType(''); setDirection('ALL'); setPaymentMode('ALL'); setQuickCheckKg('') }}
          sx={{ alignSelf: 'flex-end', background: '#fff', color: intelColors.red, border: `1px solid ${intelColors.red}`, borderRadius: '5px', padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', mb: '1px', '&:hover': { background: '#fff5f5' } }}
        >
          Reset
        </Box>
      </Box>

      {qcActive && (
        <Box sx={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '6px', p: '10px 16px', mb: '12px', fontSize: 12.5, color: '#2e7d32' }}>
          <strong>{qcMatched}</strong> courier code{qcMatched !== 1 ? 's' : ''} can handle a package of <strong>{qcKg} kg</strong> dead weight — highlighted in green below.
        </Box>
      )}

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>
          {err}
        </Box>
      )}

      <IntelTable
        title='Courier Account Weight & Dimension Limits'
        action={<Badge color='grey'>{formatNumber(rows.length)} courier code{rows.length === 1 ? '' : 's'}</Badge>}
        loading={loading}
        columns={columns}
        rows={rows}
        rowKey={(r, i) => r.cpAccountCode || i}
        rowStyle={(r) => (qcActive && (r.maxDeadWeightKg || 0) >= qcKg ? { background: '#e8f5e9' } : undefined)}
        pageSize={50}
      />
    </IntelLayout>
  )
}
